import type {
  Account,
  AccountCookieSessionImportRequest,
  AccountLoginBatchCreateRequest,
  AccountLoginBatchResponse,
  AccountLoginJob,
  AccountLoginJobCreateRequest,
  AccountLoginJobResponse,
  ApiKey,
  BrowserExtensionPairing,
  GoogleMaxBatchCreateRequest,
  GoogleMaxBatchResponse,
  ListResponse,
  LogEntry,
  MetricSample,
  Overview,
  Settings,
  Task,
  Worker,
} from "./types";
import { normalizeGoogleMaxBatchResponse } from "./googleMaxBatch";

const API_ROOT = import.meta.env.VITE_API_ROOT || "/api/admin";
const TOKEN_KEY = "higgsfield2api.admin.token";

export class ApiError extends Error {
  status: number;
  details: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

export const auth = {
  token: () => sessionStorage.getItem(TOKEN_KEY),
  setToken: (token: string) => sessionStorage.setItem(TOKEN_KEY, token),
  clear: () => sessionStorage.removeItem(TOKEN_KEY),
  isAuthenticated: () => Boolean(sessionStorage.getItem(TOKEN_KEY)),
};

interface RequestOptions extends RequestInit {
  query?: Record<string, string | number | boolean | null | undefined>;
  public?: boolean;
  responseType?: "json" | "text";
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`${API_ROOT}${path}`, window.location.origin);
  Object.entries(options.query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") url.searchParams.set(key, String(value));
  });

  const headers = new Headers(options.headers);
  if (!options.public) {
    const token = auth.token();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Accept", options.responseType === "text" ? "text/plain, application/json" : "application/json");

  let response: Response;
  try {
    response = await fetch(url, { ...options, headers });
  } catch (error) {
    // Browser/network exceptions are commonly English (for example
    // "Failed to fetch"). Keep the management console's public error
    // surface Chinese and do not echo transport internals.
    throw new ApiError(localizeClientError(error), 0, error);
  }
  const contentType = response.headers.get("content-type") || "";
  let body: unknown;
  try {
    body = response.status === 204
      ? null
      : contentType.includes("application/json")
        ? await response.json()
        : await response.text();
  } catch (error) {
    throw new ApiError("服务响应格式无效，请稍后重试", response.status, error);
  }

  if (!response.ok) {
    if (response.status === 401 && !options.public) {
      auth.clear();
      window.dispatchEvent(new CustomEvent("admin:unauthorized"));
    }
    const detail = typeof body === "object" && body !== null
      ? (body as Record<string, unknown>).detail || (body as Record<string, unknown>).message
      : body;
    throw new ApiError(localizeClientError(detail, response.status), response.status, body);
  }
  if (options.responseType === "text" && typeof body !== "string") return JSON.stringify(body) as T;
  return body as T;
}

function localizeClientError(value: unknown, status = 500): string {
  const text = typeof value === "string" ? value.trim() : "";
  // Trust only Chinese server copy. Unknown provider/browser text is not a
  // stable client contract and may contain implementation details.
  if (/[\u3400-\u9fff]/.test(text)) return text.slice(0, 4096);
  const lowered = text.toLowerCase();
  if (/(failed to fetch|networkerror|network request failed|load failed|offline)/.test(lowered)) {
    return "网络连接失败，请检查网络后重试";
  }
  if (/(timeout|timed out|超时)/.test(lowered)) {
    return "请求超时，请稍后重试";
  }
  if (status === 401) return "认证信息无效或已过期，请重新登录";
  if (status === 403) return "没有权限执行此操作";
  if (status === 404) return "请求的资源不存在";
  if (status === 429) return "请求过于频繁，请稍后重试";
  if (status >= 500 || status === 0) return "服务暂时不可用，请稍后重试";
  if (status >= 400) return "请求无效，请检查后重试";
  return "请求处理失败，请稍后重试";
}

function jsonBody(value: unknown): string {
  return JSON.stringify(value);
}

function normalizeList<T>(value: unknown): ListResponse<T> {
  if (Array.isArray(value)) return { items: value as T[], total: value.length };
  const object = (value || {}) as Record<string, unknown>;
  const items = (object.items || object.data || object.results || []) as T[];
  return {
    items: Array.isArray(items) ? items : [],
    total: Number(object.total ?? object.count ?? (Array.isArray(items) ? items.length : 0)),
  };
}

function normalizeOverview(value: unknown): Overview {
  const source = (value || {}) as Record<string, unknown>;
  const rawTasks = (source.tasks || {}) as Record<string, number>;
  const rawAccounts = (source.accounts || {}) as Record<string, number>;
  const alreadyNormalized = source.tasks && typeof source.tasks === "object" && "raw" in (source.tasks as Record<string, unknown>);
  if (alreadyNormalized) {
    const overview = source as unknown as Overview;
    return {
      ...overview,
      recent_failures: Array.isArray(overview.recent_failures)
        ? overview.recent_failures.map((task) => normalizeTask(task as Task & Record<string, unknown>))
        : [],
    };
  }
  const taskTotal = Object.values(rawTasks).reduce((sum, count) => sum + Number(count || 0), 0);
  const accountTotal = Object.values(rawAccounts).reduce((sum, count) => sum + Number(count || 0), 0);
  const failed = Number(rawTasks.FAILED || 0) + Number(rawTasks.CANCELLED || 0) + Number(rawTasks.TIMED_OUT || 0);
  return {
    accounts: {
      total: accountTotal,
      active: Number(rawAccounts.ACTIVE || 0),
      active_capacity: Number(source.capacity ?? source.configured_account_capacity ?? 0),
      configured_capacity: Number(source.configured_account_capacity ?? source.capacity ?? 0),
      browser_capacity: Number(source.browser_account_capacity ?? source.capacity ?? 0),
      occupied_capacity: Number(source.occupied_slots ?? 0),
      reauth_required: Number(rawAccounts.REAUTH_REQUIRED || 0),
    },
    tasks: {
      total: taskTotal,
      queued: Number(source.queue_eligible || 0),
      in_progress: Number(source.occupied_slots ?? rawTasks.RUNNING ?? 0),
      completed: Number(rawTasks.SUCCEEDED || 0),
      failed,
      raw: rawTasks,
    },
    workers: { live: Number(source.live_workers || 0), stale: 0 },
    throughput: { completed_last_hour: Number(source.succeeded_last_hour || 0), failed_last_hour: 0 },
    recent_failures: [],
  };
}

function normalizeTask(task: Task & Record<string, unknown>): Task {
  const rawError = task.error;
  const errorRecord = rawError && typeof rawError === "object" && !Array.isArray(rawError)
    ? { ...(rawError as Record<string, unknown>) }
    : null;
  const reason = task.error_reason ?? errorRecord?.reason;
  if (reason) {
    errorRecord && (errorRecord.reason = reason);
  }
  const normalizedError = reason
    ? { message: task.error_message || errorRecord?.message || String(rawError || ""), reason, code: errorRecord?.code || task.error_code }
    : task.error_message || rawError || null;
  return {
    ...task,
    status: String(task.internal_status || task.status || "QUEUED").toUpperCase(),
    result_url: String(task.video_url || task.result_url || task.url || "") || null,
    error: normalizedError,
    attempt_count: Number(task.attempt_count || task.create_attempts || task.poll_attempts || 0),
    parameters: (task.parameters || task.metadata || {}) as Record<string, unknown>,
  } as Task;
}

function normalizeAccountLoginBatch(value: unknown): AccountLoginBatchResponse {
  const source = (value || {}) as Record<string, unknown>;
  const rawItems = Array.isArray(source.items) ? source.items : [];
  const rawJobs = Array.isArray(source.jobs) ? source.jobs : [];
  const items = rawItems.map((raw): AccountLoginBatchResponse["items"][number] => {
    const item = (raw || {}) as Record<string, unknown>;
    const nested = item.job && typeof item.job === "object"
      ? item.job as AccountLoginJob
      : item as unknown as AccountLoginJob;
    return {
      email: String(item.email || nested.email || ""),
      job: nested,
      ...(item.account && typeof item.account === "object" ? { account: item.account as Account } : {}),
    };
  });
  const jobs = rawJobs.length
    ? rawJobs.filter((job): job is AccountLoginJob => Boolean(job && typeof job === "object"))
    : items.map((item) => item.job);
  if (!items.length && jobs.length) {
    return {
      items: jobs.map((job) => ({ email: String(job.email || ""), job })),
      jobs,
      total: Number(source.total ?? jobs.length),
    };
  }
  return { items, jobs, total: Number(source.total ?? items.length) };
}

function parsePrometheus(text: string): MetricSample[] {
  const samples: MetricSample[] = [];
  for (const line of text.split("\n")) {
    if (!line || line.startsWith("#")) continue;
    const match = line.match(/^([a-zA-Z_:][a-zA-Z0-9_:]*)(?:\{([^}]*)\})?\s+(-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?)$/);
    if (!match) continue;
    const labels: Record<string, string> = {};
    for (const label of (match[2] || "").matchAll(/([a-zA-Z_][a-zA-Z0-9_]*)="((?:\\.|[^"])*)"/g)) {
      labels[label[1]] = label[2].replace(/\\n/g, "\n").replace(/\\"/g, '"').replace(/\\\\/g, "\\");
    }
    samples.push({ name: match[1], labels, value: Number(match[3]) });
  }
  return samples;
}

export const api = {
  async login(username: string, password: string): Promise<string> {
    const value = await request<Record<string, unknown>>("/auth/login", {
      method: "POST",
      public: true,
      body: jsonBody({ username, password }),
    });
    const token = String(value.access_token || value.token || "");
    if (!token || value.success === false) throw new ApiError(String(value.message || "用户名或密码错误"), 401, value);
    auth.setToken(token);
    return token;
  },
  async logout(): Promise<void> {
    try {
      await request("/auth/logout", { method: "POST" });
    } finally {
      auth.clear();
    }
  },
  async overview(signal?: AbortSignal): Promise<Overview> {
    return normalizeOverview(await request("/overview", { signal }));
  },
  async accounts(signal?: AbortSignal): Promise<ListResponse<Account>> {
    const page = normalizeList<Account>(await request("/accounts", { signal, query: { limit: 5000 } }));
    return {
      ...page,
      items: page.items.map((account) => ({
        ...account,
        active_count: Number(account.active_count ?? account.occupied_slots ?? 0),
      })),
    };
  },
  importAccount: (payload: Record<string, unknown>) => request<{ success?: boolean; account?: Account; validation_pending?: boolean; validation_error?: string | null }>("/accounts", {
    method: "POST",
    body: jsonBody(payload),
  }),
  createBrowserExtensionPairing: () => request<BrowserExtensionPairing>("/browser-extension/pairings", {
    method: "POST",
  }),
  revokeBrowserExtensionPairings: () => request<{ success: boolean }>("/browser-extension/pairings", {
    method: "DELETE",
  }),
  createAccountLoginJob: (payload: AccountLoginJobCreateRequest) => request<AccountLoginJobResponse>("/account-login-jobs", {
    method: "POST",
    body: jsonBody(payload),
  }),
  createAccountCookieSessionImport: (payload: AccountCookieSessionImportRequest) => request<AccountLoginJobResponse>("/account-cookie-imports", {
    method: "POST",
    body: jsonBody(payload),
  }),
  async createAccountLoginJobsBatch(payload: AccountLoginBatchCreateRequest): Promise<AccountLoginBatchResponse> {
    return normalizeAccountLoginBatch(await request("/account-login-jobs/batch", {
      method: "POST",
      body: jsonBody(payload),
    }));
  },
  async accountLoginJobsStatus(ids: Array<string | number>, signal?: AbortSignal): Promise<AccountLoginBatchResponse> {
    return normalizeAccountLoginBatch(await request("/account-login-jobs/status", {
      method: "POST",
      body: jsonBody({ ids }),
      signal,
    }));
  },
  accountLoginJob: (id: string | number, signal?: AbortSignal) => request<AccountLoginJobResponse>(`/account-login-jobs/${id}`, { signal }),
  async createGoogleMaxBatch(payload: GoogleMaxBatchCreateRequest): Promise<GoogleMaxBatchResponse> {
    return normalizeGoogleMaxBatchResponse(await request("/google-max-batches", {
      method: "POST",
      body: jsonBody(payload),
    }));
  },
  async googleMaxBatch(id: string, signal?: AbortSignal): Promise<GoogleMaxBatchResponse> {
    return normalizeGoogleMaxBatchResponse(await request(`/google-max-batches/${encodeURIComponent(id)}`, { signal }));
  },
  validateAccount: (id: Account["id"]) => request<{ success?: boolean; validation_pending?: boolean }>(`/accounts/${id}/validate`, { method: "POST" }),
  updateAccount: (id: Account["id"], payload: Record<string, unknown>) => request<{ success?: boolean; validation_pending?: boolean }>(`/accounts/${id}`, {
    method: "PUT",
    body: jsonBody(payload),
  }),
  setAccountStatus: (id: Account["id"], active: boolean) => request(`/accounts/${id}/status`, {
    method: "POST",
    body: jsonBody({ enabled: active }),
  }),
  async tasks(query: Record<string, string | number | undefined>, signal?: AbortSignal): Promise<ListResponse<Task>> {
    const page = normalizeList<Task & Record<string, unknown>>(await request("/tasks", { query, signal }));
    return {
      ...page,
      items: page.items.map(normalizeTask),
    };
  },
  async task(id: string, signal?: AbortSignal): Promise<Task> {
    return normalizeTask(await request<Task & Record<string, unknown>>(`/tasks/${encodeURIComponent(id)}`, { signal }));
  },
  cancelTask: (id: string) => request(`/tasks/${id}/cancel`, { method: "POST" }),
  retryTask: (id: string) => request(`/tasks/${id}/retry`, { method: "POST" }),
  reconcileTask: (id: string, remoteJobSetId: string, remoteJobId?: string) => request(`/tasks/${id}/reconcile`, {
    method: "POST",
    body: jsonBody({ remote_job_set_id: remoteJobSetId, ...(remoteJobId ? { remote_job_id: remoteJobId } : {}) }),
  }),
  confirmTaskNotCreated: (id: string) => request(`/tasks/${id}/confirm-not-created`, { method: "POST" }),
  async apiKeys(signal?: AbortSignal): Promise<ListResponse<ApiKey>> {
    const page = normalizeList<ApiKey & Record<string, unknown>>(await request("/api-keys", { signal }));
    return {
      ...page,
      items: page.items.map((key) => ({
        ...key,
        rate_limit_per_minute: Number(key.rate_limit_per_minute || key.minute_limit) || null,
        daily_quota: Number(key.daily_quota || key.daily_limit) || null,
        monthly_quota: Number(key.monthly_quota || key.monthly_limit) || null,
      })),
    };
  },
  createApiKey: (payload: Record<string, unknown>) => request<{ success?: boolean; api_key?: ApiKey; key?: string }>("/api-keys", {
    method: "POST",
    body: jsonBody(payload),
  }),
  deleteApiKey: (id: ApiKey["id"]) => request(`/api-keys/${id}`, { method: "DELETE" }),
  async logs(query: Record<string, string | number | undefined>, signal?: AbortSignal): Promise<ListResponse<LogEntry>> {
    const page = normalizeList<LogEntry & Record<string, unknown>>(await request("/logs", { query, signal }));
    const items = page.items.map((log) => {
      const status = Number(log.status_code || 0);
      return {
        ...log,
        level: log.level || (status >= 500 ? "ERROR" : status >= 400 ? "WARNING" : "INFO"),
        event: log.event || `${log.source || "Request"}.${log.method || "EVENT"}`,
        message: log.message || `${log.method || "EVENT"} ${log.url || ""}${status ? ` -> ${status}` : ""}`,
        context: log.context || { request_body: log.request_body, response_body: log.response_body, duration_ms: log.duration_ms },
      } as LogEntry;
    });
    return {
      ...page,
      items,
    };
  },
  async settings(signal?: AbortSignal): Promise<Settings> {
    const value = await request<Settings & { values?: Settings }>("/settings", { signal });
    return value.values || value;
  },
  async updateSettings(payload: Settings): Promise<{ values: Settings; restart_required: boolean }> {
    const value = await request<Settings & { values?: Settings; restart_required?: boolean }>("/settings", { method: "PUT", body: jsonBody({ values: payload }) });
    return { values: value.values || value, restart_required: Boolean(value.restart_required) };
  },
  async workers(signal?: AbortSignal): Promise<ListResponse<Worker>> {
    const page = normalizeList<Worker & Record<string, unknown>>(await request("/workers", { signal }));
    return {
      ...page,
      items: page.items.map((worker) => {
        const metadata = (worker.metadata || {}) as Record<string, unknown>;
        const accountIds = Array.isArray(metadata.account_ids) ? metadata.account_ids : [];
        const inFlight = Number(worker.in_flight ?? metadata.in_flight ?? 0);
        const browserAccounts = Number(worker.browser_accounts ?? metadata.browser_accounts ?? accountIds.length);
        const browserAccountLimit = Number(worker.browser_account_limit ?? metadata.max_accounts ?? 0);
        return {
          ...worker,
          id: String(worker.id || worker.owner || worker.worker_id),
          worker_id: String(worker.worker_id || worker.owner || worker.id),
          pid: Number(worker.pid || worker.process_id || 0) || undefined,
          hostname: String(worker.hostname || metadata.hostname || "") || undefined,
          active_leases: Number(worker.active_leases ?? metadata.active_leases ?? 0),
          concurrency: Number(worker.concurrency ?? metadata.concurrency ?? 0) || undefined,
          in_flight: inFlight,
          browser_accounts: browserAccounts,
          browser_account_limit: browserAccountLimit,
          last_heartbeat_at: (worker.last_heartbeat_at || worker.heartbeat_at) as string | number,
        } as Worker;
      }),
    };
  },
  async metrics(signal?: AbortSignal): Promise<MetricSample[]> {
    const text = await request<string>("/metrics", { signal, responseType: "text" });
    try {
      const object = JSON.parse(text) as Record<string, unknown>;
      const source = (object.metrics || object) as Record<string, unknown>;
      return flattenMetrics(source);
    } catch {
      return parsePrometheus(text);
    }
  },
};

function flattenMetrics(value: Record<string, unknown>, prefix = "higgsfield"): MetricSample[] {
  const result: MetricSample[] = [];
  for (const [key, raw] of Object.entries(value)) {
    const name = `${prefix}_${key}`.replace(/[^a-zA-Z0-9_:]/g, "_");
    if (typeof raw === "number") {
      result.push({ name, labels: {}, value: raw });
    } else if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      const record = raw as Record<string, unknown>;
      if (Object.values(record).every((item) => typeof item === "number")) {
        for (const [label, number] of Object.entries(record)) result.push({ name, labels: { status: label }, value: Number(number) });
      } else {
        result.push(...flattenMetrics(record, name));
      }
    }
  }
  return result;
}
